/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { ListModal } from 'src/core/components/ListModal';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';

export type DomainModelsModalProps = BasicModalProps<DomainModelsModalDto> & {
    /**
     * The prebuilt domain type.
     */
    modelsType: 'intents' | 'entities';
};

export type DomainModelsModalDto = {
    /**
     * The models that were selected in the modal.
     */
    selectedModels: DomainModel[];
};

const messages = defineMessages({
    modalTitle: {
        id: 'domains.domainModelsModal.modalTitle',
        defaultMessage: 'Add prebuilt domain models'
    },
    modalSubtitle: {
        id: 'domains.domainModelsModal.modalSubtitle',
        defaultMessage: `Prebuilt domain models are parts of prebuilt domains.
We recommend using models from the same domain when possible since they work better together.`
    },
    searchPlaceholder: {
        id: 'domains.domainModelsModal.searchPlaceholder',
        defaultMessage: 'Search built-in models ...'
    },
    columnHeader: {
        id: 'domains.domainModelsModal.columnHeader',
        defaultMessage: 'Name'
    },
    addModelToastStart: {
        id: 'domains.domainModelsModal.addModelToastStart',
        defaultMessage: 'Adding domain ...'
    },
    addModelToastSuccess: {
        id: 'domains.domainModelsModal.addModelToastSuccess',
        defaultMessage: 'Add domain model completed'
    },
    addModelToastFailed: {
        id: 'domains.domainModelsModal.addModelToastFailed',
        defaultMessage: 'Add domain model failed'
    }
});

/**
 * Displays a list modal with prebuilt domain models in the list.
 *
 * @param props The list modal properties.
 */
export const DomainModelsModal = withModalMounter(
    observer((props: DomainModelsModalProps) => {
        const { modelsType, onSubmit, ...rest } = props;
        const { localize } = useLocalization();
        const { rootStore, dispatcher } = useGlobalState();
        const { announcedComponent, announceAction } = useAnnounced();
        const { entityStoreHouse, intentStoreHouse } = rootStore.currentLuisAppStore.currentAppVersionStore;
        const {
            culture,
            domainStoreHouse: { items, isLoading }
        } = rootStore.currentLuisAppStore;

        React.useEffect(() => {
            dispatcher.dispatch('loadDomains', { culture });
        }, []);

        const addDomainTracker = useProgressTracker({
            onComplete: () => {
                announceAction(localize(messages.addModelToastSuccess));
                onSubmit();
            }
        });

        let prebuiltDomainModels;
        if (modelsType === 'entities') {
            prebuiltDomainModels = flatten(
                items.map(domain => domain.entities.map(e => ({
                        domainName: domain.name,
                        modelName: e.name,
                        name: `${domain.name}.${e.name}`
                    })))
            ).filter(entity => !entityStoreHouse.findByName(entity.name));
        } else {
            prebuiltDomainModels = flatten(
                items.map(domain => domain.intents.map(e => ({
                        domainName: domain.name,
                        modelName: e.name,
                        name: `${domain.name}.${e.name}`
                    })))
            ).filter(entity => !intentStoreHouse.findByName(entity.name));
        }

        const addPrebuiltDomain = ({ selectedModels }: { selectedModels: DomainModel[] }) => {
            const handlerName = modelsType === 'entities' ? 'addPrebuiltDomainEntities' : 'addPrebuiltIntents';

            dispatcher.dispatch(handlerName, {
                prebuiltDomains: selectedModels,
                tracker: addDomainTracker,
                toastData: {
                    running: { title: localize(messages.addModelToastStart) },
                    completed: { title: localize(messages.addModelToastSuccess) },
                    failed: { title: localize(messages.addModelToastFailed) }
                }
            });
        };

        return (
            <>
                <ListModal<DomainModel>
                    searchKeyParam="name"
                    isLoading={isLoading}
                    items={prebuiltDomainModels}
                    disabled={addDomainTracker.isRunning}
                    subtitle={localize(messages.modalSubtitle)}
                    columnHeader={localize(messages.columnHeader)}
                    searchPlaceholder={localize(messages.searchPlaceholder)}
                    dialogContentProps={{ title: localize(messages.modalTitle) }}
                    onSubmit={dto => addPrebuiltDomain({ selectedModels: dto.selectedItems })}
                    {...rest}
                />
                {announcedComponent}
            </>
        );
    })
);
